import React from "react";
import {
  Box,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { DESIGN_ICONS } from "../../theme/designIcons";
import {
  isNavLinkActive,
  LOCKED_TOOLTIP,
  type NavLink,
  type NavSection,
} from "./navSections";
import styles from "./NavRail.module.css";

interface NavFlyoutProps {
  id: string;
  /** The section to list; null while closed (keeps the last one for the fade). */
  section: NavSection | null;
  open: boolean;
  pathname: string;
  onSelect: (link: NavLink) => void;
}

/**
 * The 368px panel beside the rail listing a section's pages, each with an
 * icon tile, label and one-line description. The current page gets the teal
 * tint, strong teal ink and a trailing check.
 */
export const NavFlyout: React.FC<NavFlyoutProps> = ({
  id,
  section,
  open,
  pathname,
  onSelect,
}) => (
  <Box
    id={id}
    className={styles.flyout}
    data-open={open || undefined}
    aria-hidden={!open}
    aria-label={section ? `${section.label} pages` : undefined}
    role="region"
  >
    {section && (
      <>
        <Title order={2} className={styles.flyoutTitle}>
          {section.label}
        </Title>
        <Stack gap={4}>
          {section.links.map((link) => (
            <FlyoutLink
              key={link.key}
              link={link}
              active={isNavLinkActive(link, pathname)}
              tabbable={open}
              onSelect={onSelect}
            />
          ))}
        </Stack>
      </>
    )}
  </Box>
);

interface FlyoutLinkProps {
  link: NavLink;
  active: boolean;
  tabbable: boolean;
  onSelect: (link: NavLink) => void;
}

const FlyoutLink: React.FC<FlyoutLinkProps> = ({
  link,
  active,
  tabbable,
  onSelect,
}) => {
  const LinkIcon = link.icon;
  const row = (
    <UnstyledButton
      className={styles.flyoutLink}
      data-active={active || undefined}
      data-locked={link.locked || undefined}
      aria-current={active ? "page" : undefined}
      aria-disabled={link.locked || undefined}
      tabIndex={tabbable ? 0 : -1}
      onClick={link.locked ? undefined : () => onSelect(link)}
    >
      <Box component="span" className={styles.flyoutIcon}>
        <LinkIcon size={16} stroke={1.75} />
      </Box>
      <Box component="span" className={styles.flyoutText}>
        <Text span className={styles.flyoutLabel}>
          {link.label}
        </Text>
        <Text span className={styles.flyoutDesc}>
          {link.description}
        </Text>
      </Box>
      {active && (
        <Box component="span" className={styles.flyoutCheck}>
          <DESIGN_ICONS.Check size={16} />
        </Box>
      )}
    </UnstyledButton>
  );

  return link.locked ? (
    <Tooltip label={LOCKED_TOOLTIP} withArrow position="right">
      {row}
    </Tooltip>
  ) : (
    row
  );
};
